import { type FC, useEffect, useState } from "react";

import { ChangeSummary } from "./summary/ChangeSummary";

import { serverGetPublicationSummary } from "api/api-server-publishing";
import { type PublicationSummary } from "api/api-types-publishing";
import { type PublicationType } from "components/server-types";
import { formatLocalePattern } from "i18n/helpers";
import { t } from "i18n/i18n";
import { AccordionPanel } from "mds/components/AccordionPanel";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { CircleCheckIcon, LoadingDotsIcon } from "mds/icons";

interface PublicationRecordTitleProps {
  publication: PublicationType;
  summary: PublicationSummary | null;
}

const PublicationRecordTitle: FC<PublicationRecordTitleProps> = ({ publication, summary }) => {
  const publishedDate = publication.published_at
    ? formatLocalePattern(publication.published_at, "common.formats.month_day_year_time")
    : "-";

  return (
    <div className="flex items-center gap-3">
      <CircleCheckIcon className="text-moss-tint-30" size="xxxl" />
      <div className="flex flex-col">
        <h3>
          {t("publishing.publication_record.title", {
            date: publishedDate,
            publisher: publication.user_name ?? "-",
          })}
        </h3>

        <div className="body-s text-black-tint-20">
          {t("publishing.publication_record.description", {
            count: publication.linked_sections_count,
            authors: summary?.authors.join(", ") || "-",
          })}
        </div>
      </div>
    </div>
  );
};

interface PublicationRecordProps {
  publication: PublicationType;
}
export const PublicationRecord: FC<PublicationRecordProps> = ({ publication }) => {
  const [summary, setSummary] = useState<PublicationSummary | null>(null);

  const noSummary =
    !summary ||
    (summary.topics.length === 0 &&
      summary.assignments.length === 0 &&
      summary.resources.length === 0 &&
      summary.workspaces.length === 0 &&
      summary.excluded_pages.length === 0);

  useEffect(() => {
    serverGetPublicationSummary(publication.id).then(setSummary);
  }, [publication.id]);

  return (
    <AccordionPanel
      childrenWrapperClassName="gap-6"
      className="rounded-[12px] p-6 supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
      collapsedColor="light-gray"
      expandedColor="green"
      hideToggle={noSummary}
      iconPosition="end"
      iconSize="xl"
      title={<PublicationRecordTitle publication={publication} summary={summary} />}
    >
      <HorizontalDivider className="mt-6 border-moss-tint-55" />
      {summary ? (
        <ChangeSummary sectionId={publication.section_id} summary={summary} />
      ) : (
        <div className="flex justify-center py-4" key="loading">
          <LoadingDotsIcon />
        </div>
      )}
    </AccordionPanel>
  );
};
